import { useEffect, useState } from 'react'
import { Alert, Stack, Text } from '@mantine/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  aplicarEmergencia, confirmarPedidoDeCodigo, criarPedidoDeCodigo, type AcaoDeWaf, type ModoCodigo,
  type PedidoCriado, type PedidoNoCodigo,
} from '@/api/waf'
import { formatApiError } from '@/api/errors'
import { useAuthStore, useCan } from '@/store/auth'
import {
  CHAVE_PEDIDO_TERRAFORM, useRetomarPedido, useTerraformChangeRequest,
} from '@/hooks/useTerraformChangeRequest'
import { ControlesDoModulo } from './ControlesDoModulo'
import { PainelPedidoNoCodigo } from './PainelPedidoNoCodigo'

/** O modo código no topo da tela: o pedido em andamento e os gestos do módulo. */
export function PainelDoCodigo({ wafId, modo }: { wafId: number; modo: ModoCodigo }) {
  const qc = useQueryClient()
  const can = useCan()
  const { user } = useAuthStore()
  const recarregar = () => qc.invalidateQueries({ queryKey: ['waf-codigo', wafId] })

  // O pedido recém-criado aparece já com o id que o POST devolve: a carga do
  // modo código relê o git e a AWS do cliente e demora, e até ela voltar a tela
  // ficava sem painel (pedido #42, prd, 15/10/2026).
  const [recemCriado, setRecemCriado] = useState<{ id: number; acao: AcaoDeWaf } | null>(null)
  // Spec 2026-10-15 §5.2: o pedido que nasceu na fila não ocupa o stage; a
  // tela diz o lugar (o 202 traz a posição) e ele aparece em "na espera".
  const [naFila, setNaFila] = useState<PedidoCriado | null>(null)
  const criar = useMutation({
    mutationFn: (body: Parameters<typeof criarPedidoDeCodigo>[1]) => criarPedidoDeCodigo(wafId, body),
    onSuccess: (r, body) => {
      if (r.estado === 'na_fila') {
        setNaFila(r)
      } else {
        setNaFila(null)
        setRecemCriado({ id: r.pedido_id, acao: body.acao })
      }
      recarregar()
    },
  })
  const confirmar = useMutation({
    mutationFn: (pedidoId: number) => confirmarPedidoDeCodigo(wafId, pedidoId),
    onSuccess: () => {
      recarregar()
      qc.invalidateQueries({ queryKey: [CHAVE_PEDIDO_TERRAFORM] })
    },
  })

  const [emergenciaAberta, setEmergenciaAberta] = useState(false)
  const emergencia = useMutation({
    mutationFn: (body: Parameters<typeof aplicarEmergencia>[1]) => aplicarEmergencia(wafId, body),
    onSuccess: () => { setEmergenciaAberta(false); recarregar() },
  })

  const ativo = modo.pedido_ativo
  const parado = modo.pedido_parado ?? null
  const recemNaCarga = recemCriado != null && (ativo?.id === recemCriado.id || parado?.id === recemCriado.id)
  const recem: PedidoNoCodigo | null = recemCriado && !ativo && !recemNaCarga ? {
    id: recemCriado.id, estado: 'rascunho', origem: 'tela_waf', waf_acao: recemCriado.acao,
    requested_by_user_id: user?.id ?? null, mr_iid: null, aprovado_no_plan: null, motivo: null,
    waf_reconciliado: [], resumo: null,
  } : null
  // Ocupa o stage: o ativo ou o recém-criado. O parado só fica à vista, com o
  // motivo e o que fazer, até um pedido novo — sem chamado, aqui é o único
  // lugar em que quem pediu o lê, e é para cá que o aviso leva (consoleai#25074).
  const ocupando = ativo ?? recem
  const pedido = ocupando ?? parado
  // O pedido repergunta sozinho (10 s, parando quando espera a pessoa); a
  // carga do modo código não, porque relê o git e a AWS do cliente. Quando o
  // estado anda, relê-se a carga uma vez: é ela que decide as travas e se
  // ainda há pedido ocupando o stage.
  const acompanhamento = useTerraformChangeRequest(pedido?.id ?? null)
  const estadoAtual = acompanhamento.data?.estado
  useEffect(() => {
    if (pedido && estadoAtual && estadoAtual !== pedido.estado) recarregar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estadoAtual])
  useEffect(() => {
    if (recemNaCarga) setRecemCriado(null)
  }, [recemNaCarga])
  const naEspera = (modo.pedidos_na_espera ?? []).filter(p => p.id !== pedido?.id)
  return (
    <Stack gap="sm">
      {naFila && (
        <Alert color="blue" variant="light" title={`Pedido #${naFila.pedido_id} na fila`}
               withCloseButton onClose={() => setNaFila(null)}>
          <Text size="sm">{fraseDaFila(naFila)}</Text>
        </Alert>
      )}
      {pedido && (
        <PainelPedidoNoCodigo
          pedido={pedido}
          parado={!ocupando}
          souRequerente={user?.id != null && user.id === pedido.requested_by_user_id}
          onConfirmar={() => confirmar.mutate(pedido.id)}
          confirmando={confirmar.isPending}
          erro={confirmar.isError ? formatApiError(confirmar.error, 'Erro ao confirmar.') : null}
          acompanhamento={acompanhamento.data ?? null}
          onDecidido={() => {
            recarregar()
            qc.invalidateQueries({ queryKey: [CHAVE_PEDIDO_TERRAFORM] })
          }}
        />
      )}
      {naEspera.map(p => (
        <PedidoNaEspera key={p.id} pedido={p} souRequerente={user?.id != null && user.id === p.requested_by_user_id}
                        onMudou={recarregar} />
      ))}
      {can('waf', 'rule:manage') && (
        <ControlesDoModulo
          campos={modo.campos}
          divergencias={modo.divergencias}
          ocupadoPor={ocupando?.id ?? null}
          enviando={criar.isPending}
          erro={criar.isError ? formatApiError(criar.error, 'Erro ao pedir a mudança.') : null}
          onEnviar={body => criar.mutate(body)}
          stage={modo.vinculo?.stage_chave ?? ''}
          emergencia={can('waf', 'emergencia') ? {
            enviando: emergencia.isPending,
            // O 409 diz o número do pedido que faz a emergência esperar.
            erro: emergencia.isError ? formatApiError(emergencia.error, 'Erro ao aplicar a emergência.') : null,
            aberta: emergenciaAberta,
            onAbrir: () => { emergencia.reset(); setEmergenciaAberta(true) },
            onFechar: () => setEmergenciaAberta(false),
            onAplicar: body => emergencia.mutate(body),
          } : undefined}
        />
      )}
    </Stack>
  )
}

/** "2º na fila, atrás do pedido #41 (planejando)" — e o motivo, com a fila parada. */
export function fraseDaFila(r: PedidoCriado): string {
  const partes = [r.posicao != null ? `${r.posicao}º na fila` : 'Na fila']
  if (r.atras_de) partes[0] += `, atrás do pedido #${r.atras_de.id} (${r.atras_de.estado})`
  let frase = partes[0] + '.'
  if (r.fila_pausada) frase += ` A fila está parada: ${r.fila_pausada}.`
  return frase + ' Ele roda quando chegar a vez, sem precisar pedir de novo.'
}

/** Um pedido da tela que espera sem ocupar o stage: na fila, ou o livre
 * guardado, com o "Quero seguir" de quem pediu (spec 2026-10-15 D3). */
function PedidoNaEspera({ pedido, souRequerente, onMudou }: {
  pedido: PedidoNoCodigo & { posicao: number | null }
  souRequerente: boolean
  onMudou: () => void
}) {
  const acompanhamento = useTerraformChangeRequest(pedido.id)
  const retomar = useRetomarPedido()
  return (
    <PainelPedidoNoCodigo
      pedido={pedido}
      souRequerente={souRequerente}
      onConfirmar={() => {}}
      confirmando={false}
      erro={retomar.isError ? formatApiError(retomar.error, 'Erro ao retomar o pedido.') : null}
      acompanhamento={acompanhamento.data ?? null}
      onRetomar={() => retomar.mutate(pedido.id, { onSettled: onMudou })}
      retomando={retomar.isPending}
    />
  )
}
